// useShiftSpaceRotateHold — Shift+Space を押している間だけ「回転ドラッグ」に一時切り替える
// フック (canvas-rotation 段取り 2、docs/_wip/canvas-rotation-2026-10-03.md「PC (CSP 準拠)」)。
//
// `Space` だけなら従来どおり手のひら (useSpaceHoldPan / ClassicLayout の Space hold が管轄、
// どちらも Shift 同時押しは pan 開始条件から除外している) なので、このフックは
// 「Space を押したまま Shift も押している」状態だけを独立に追う。Shift を放しても Space が
// 残っていれば手のひらに戻る (= 呼び出し側が `rotateHeld` を優先し、false なら従来の pan 判定)。
//
// 判定 (入力欄フォーカス中は通常入力、IME 中は無視) は useSpaceHoldPan の
// `isTextInputFocus` と同じ。keyup / blur / hidden で必ず false に戻す (押しっぱなし誤検知防止)。

import { useEffect, useState } from 'react';

import { isTextInputFocus } from './useSpaceHoldPan.js';

/**
 * keydown が「Shift+Space の回転ホールド」のトリガか。Space + Shift 必須、Ctrl/Meta/Alt は
 * 別ショートカット衝突回避で対象外、文字入力系フォーカス中も対象外 (純関数、DOM 不要)。
 */
export const isShiftSpaceKeyDown = (e: {
  readonly code: string;
  readonly shiftKey: boolean;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
  readonly altKey: boolean;
  readonly target: EventTarget | null;
}): boolean => {
  if (e.code !== 'Space') return false;
  if (!e.shiftKey) return false;
  if (e.ctrlKey || e.metaKey || e.altKey) return false;
  if (isTextInputFocus(e.target)) return false;
  return true;
};

/** Space / Shift それぞれの押下状態。両方 true の間だけ回転ホールド。 */
export interface RotateHoldKeys {
  readonly space: boolean;
  readonly shift: boolean;
}

export const ROTATE_HOLD_IDLE: RotateHoldKeys = { space: false, shift: false };

/**
 * keydown / keyup ごとの押下状態の遷移 (純関数)。押す順序は問わない
 * (Shift → Space でも Space → Shift でも回転。CSP と同じ)。
 * Space → Shift の順だと最後の keydown は Shift なので、Space keydown の `shiftKey`
 * だけを見る判定では拾えない (staging 実機 fb 2026-10-03) → 2 キーを別々に追う。
 * Space は手のひらと同じ条件 (入力欄フォーカス中・Ctrl/Meta/Alt 同時押しは対象外) で数える。
 */
export const nextRotateHoldKeys = (
  prev: RotateHoldKeys,
  type: 'keydown' | 'keyup',
  e: Parameters<typeof isShiftSpaceKeyDown>[0] & { readonly key: string },
): RotateHoldKeys => {
  if (type === 'keyup') {
    if (e.code === 'Space') return { ...prev, space: false };
    if (e.key === 'Shift') return { ...prev, shift: false };
    return prev;
  }
  if (e.key === 'Shift') return { ...prev, shift: true };
  if (e.code === 'Space') {
    // `{ ...e }` は使わない: DOM の KeyboardEvent は code / ctrlKey / target 等が prototype
    // の getter なので spread でコピーされず、常に不一致になる (staging 実機 fb 2026-10-03)。
    const asShiftSpace = {
      code: e.code,
      shiftKey: true,
      ctrlKey: e.ctrlKey,
      metaKey: e.metaKey,
      altKey: e.altKey,
      target: e.target,
    };
    if (isShiftSpaceKeyDown(asShiftSpace)) return { space: true, shift: prev.shift || e.shiftKey };
    return prev;
  }
  return prev;
};

/**
 * Shift と Space を両方押している間 true (押す順序は問わない)。どちらかが離されたら
 * false に戻す。
 */
export const useShiftSpaceRotateHold = (): boolean => {
  const [held, setHeld] = useState<boolean>(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let keys = ROTATE_HOLD_IDLE;
    const update = (next: RotateHoldKeys): void => {
      keys = next;
      setHeld(next.space && next.shift);
    };
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.isComposing) return;
      const next = nextRotateHoldKeys(keys, 'keydown', e);
      // page scroll / フォーカス中ボタンの Space 起動を抑止 (Space hold pan と同じ)。
      if (e.code === 'Space' && next.space && next.shift) e.preventDefault();
      update(next);
    };
    const onKeyUp = (e: KeyboardEvent): void => update(nextRotateHoldKeys(keys, 'keyup', e));
    const onBlur = (): void => update(ROTATE_HOLD_IDLE);
    const onVisibility = (): void => {
      if (document.visibilityState === 'hidden') update(ROTATE_HOLD_IDLE);
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);
  return held;
};
